import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, isSameMonth, parseISO,
} from "date-fns";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Users2, Plus, Trash2, ChevronLeft, ChevronRight, Search, Phone,
  Pencil, X, Save, Eraser,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const PALETTE = [
  "#059669", "#dc2626", "#7c3aed", "#0891b2", "#d97706",
  "#db2777", "#0d9488", "#4f46e5", "#65a30d", "#ea580c",
];

const initialsFromName = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.slice(0, 2) || "?").toUpperCase();
};

export default function OnCallSchedulePage({ facilityId, facilityLabel }) {
  const [managers, setManagers] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedMgr, setSelectedMgr] = useState(null);
  const [range, setRange] = useState({ start: null, end: null });
  const [busy, setBusy] = useState(false);

  const monthKey = format(month, "yyyy-MM");

  const loadManagers = useCallback(async () => {
    if (!facilityId) return;
    try {
      const r = await api.get(`/oncall/managers?facility_id=${facilityId}`);
      setManagers(r.data || []);
    } catch (e) { /* ignore */ }
  }, [facilityId]);

  const loadCalendar = useCallback(async () => {
    if (!facilityId) return;
    try {
      const r = await api.get(`/oncall/calendar?facility_id=${facilityId}&month=${monthKey}`);
      setCalendar(r.data || []);
    } catch (e) { /* ignore */ }
  }, [facilityId, monthKey]);

  useEffect(() => { loadManagers(); }, [loadManagers]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  useEffect(() => {
    if (!selectedMgr && managers.length > 0) setSelectedMgr(managers[0].id);
  }, [managers, selectedMgr]);

  const mgrById = useMemo(() => {
    const m = new Map();
    managers.forEach(x => m.set(x.id, x));
    return m;
  }, [managers]);

  const calByDate = useMemo(() => {
    const m = new Map();
    calendar.forEach(c => m.set(c.date, c.manager_id));
    return m;
  }, [calendar]);

  const days = useMemo(() => {
    const first = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
    const last = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
    const out = [];
    for (let d = first; d <= last; d = addDays(d, 1)) out.push(d);
    return out;
  }, [month]);

  const removeManager = async (mid) => {
    if (!window.confirm("Remove this manager from the on-call roster? Their calendar assignments will be cleared.")) return;
    try {
      await api.delete(`/oncall/managers/${mid}`);
      setManagers(prev => prev.filter(m => m.id !== mid));
      loadCalendar();
      toast.success("Removed");
    } catch (e) {
      toast.error("Couldn't remove");
    }
  };

  const handleDayClick = async (d) => {
    if (!selectedMgr) { toast.error("Select or add an on-call manager first"); return; }
    const iso = format(d, "yyyy-MM-dd");
    // Range mode: shift-click sets end date
    if (range.start && !range.end) {
      const start = range.start < iso ? range.start : iso;
      const end = range.start < iso ? iso : range.start;
      setBusy(true);
      try {
        await api.post("/oncall/calendar", {
          facility_id: facilityId, start_date: start, end_date: end, manager_id: selectedMgr,
        });
        setRange({ start: null, end: null });
        loadCalendar();
      } catch (e) {
        toast.error("Save failed");
      } finally { setBusy(false); }
      return;
    }
    // Single click: toggle — if the same manager is already assigned, clear; else assign
    const existingMgrId = calByDate.get(iso);
    setBusy(true);
    try {
      await api.post("/oncall/calendar", {
        facility_id: facilityId, start_date: iso, end_date: iso,
        manager_id: (existingMgrId === selectedMgr) ? null : selectedMgr,
      });
      loadCalendar();
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const handleDayContext = (d, e) => {
    e.preventDefault();
    // Start range
    setRange({ start: format(d, "yyyy-MM-dd"), end: null });
    toast("Range start set — click the end date to assign a range");
  };

  const clearRange = async () => {
    if (!window.confirm("Clear all on-call assignments in this month?")) return;
    setBusy(true);
    try {
      const first = format(startOfMonth(month), "yyyy-MM-dd");
      const last = format(endOfMonth(month), "yyyy-MM-dd");
      await api.post("/oncall/calendar", {
        facility_id: facilityId, start_date: first, end_date: last, manager_id: null,
      });
      loadCalendar();
      toast.success("Cleared");
    } catch (e) {
      toast.error("Couldn't clear");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 pb-6" data-testid="oncall-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <div className="font-display font-bold text-2xl sm:text-3xl">On-Call Schedule</div>
          <div className="text-sm text-slate-500 mt-0.5">{facilityLabel || "Facility"}</div>
        </div>
        <Button onClick={() => { setEditing(null); setAddOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="oncall-add-mgr">
          <Plus size={14} className="mr-1" /> Add on-call manager
        </Button>
      </div>

      {/* Managers roster */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Users2 size={16} className="text-slate-500" />
          <div className="font-display font-semibold">Managers on the on-call list</div>
        </div>
        {managers.length === 0 ? (
          <div className="text-sm text-slate-500">
            No managers yet. Add one to start assigning days on the calendar.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="oncall-mgr-list">
            {managers.map((m) => (
              <div
                key={m.id}
                className={`rounded-xl border p-3 flex items-center gap-3 transition cursor-pointer ${
                  selectedMgr === m.id ? "border-slate-900 ring-2 ring-slate-900/10 bg-slate-50" : "border-slate-200 hover:border-slate-300"
                }`}
                onClick={() => setSelectedMgr(m.id)}
                data-testid={`oncall-mgr-${m.id}`}
              >
                <div
                  className="w-11 h-11 rounded-full grid place-items-center text-white font-bold text-sm shrink-0"
                  style={{ background: m.color }}
                >
                  {m.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{m.name}</div>
                  <div className="text-xs text-slate-500 tabular-nums truncate">{m.phone}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className="p-1.5 rounded hover:bg-slate-200 text-slate-500"
                    onClick={(e) => { e.stopPropagation(); setEditing(m); setAddOpen(true); }}
                    title="Edit"
                    data-testid={`oncall-mgr-edit-${m.id}`}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    className="p-1.5 rounded hover:bg-red-100 text-red-600"
                    onClick={(e) => { e.stopPropagation(); removeManager(m.id); }}
                    title="Remove"
                    data-testid={`oncall-mgr-del-${m.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Monthly calendar */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="p-3 sm:p-4 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          <button
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            onClick={() => setMonth(addMonths(month, -1))}
            data-testid="oncall-prev-month"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="font-display font-semibold text-lg tabular-nums min-w-[140px] text-center">
            {format(month, "MMMM yyyy")}
          </div>
          <button
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            onClick={() => setMonth(addMonths(month, 1))}
            data-testid="oncall-next-month"
          >
            <ChevronRight size={16} />
          </button>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div className="text-xs text-slate-500">Painting with:</div>
            <Select value={selectedMgr || ""} onValueChange={setSelectedMgr}>
              <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="oncall-brush">
                <SelectValue placeholder="Pick a manager" />
              </SelectTrigger>
              <SelectContent>
                {managers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: m.color }} />
                      {m.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="text-xs" onClick={clearRange} disabled={busy} data-testid="oncall-clear-month">
              <Eraser size={12} className="mr-1" /> Clear month
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
            <div key={d} className="p-2 text-[10px] uppercase tracking-widest text-slate-500 text-center font-semibold">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 divide-x divide-slate-100">
          {days.map((d) => {
            const iso = format(d, "yyyy-MM-dd");
            const outOfMonth = !isSameMonth(d, month);
            const mgrId = calByDate.get(iso);
            const mgr = mgrId ? mgrById.get(mgrId) : null;
            const isRangeStart = range.start === iso;
            return (
              <button
                key={iso}
                type="button"
                className={`min-h-[76px] sm:min-h-[92px] p-2 border-b border-slate-100 relative text-left transition ${
                  outOfMonth ? "bg-slate-50/40 text-slate-400" : "bg-white hover:bg-blue-50/30"
                } ${isRangeStart ? "ring-2 ring-blue-500 ring-inset" : ""}`}
                onClick={() => handleDayClick(d)}
                onContextMenu={(e) => handleDayContext(d, e)}
                data-testid={`oncall-day-${iso}`}
                disabled={busy}
              >
                <div className={`text-xs font-semibold tabular-nums ${outOfMonth ? "text-slate-300" : ""}`}>
                  {format(d, "d")}
                </div>
                {mgr && (
                  <div
                    className="mt-1 rounded-md px-1.5 py-1 text-white text-[11px] font-bold flex items-center gap-1.5 shadow-sm"
                    style={{ background: mgr.color }}
                    title={`${mgr.name} · ${mgr.phone}`}
                  >
                    <span className="rounded-full bg-white/25 px-1 py-0.5 text-[10px]">
                      {mgr.initials}
                    </span>
                    <span className="truncate">{mgr.name.split(" ")[0]}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="p-3 border-t border-slate-200 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
          <span><b>Click</b> a day to toggle on-call for the selected manager.</span>
          <span><b>Right-click</b> a day → then click a second day = assign a range.</span>
          <span className="ml-auto">Cutover happens at local midnight — the dashboard flips automatically.</span>
        </div>
      </div>

      <ManagerEditor
        open={addOpen}
        onClose={() => { setAddOpen(false); setEditing(null); }}
        facilityId={facilityId}
        editing={editing}
        onSaved={(m) => {
          if (editing) setManagers(prev => prev.map(x => x.id === m.id ? m : x));
          else { setManagers(prev => [...prev, m]); setSelectedMgr(m.id); }
          loadCalendar();
        }}
      />
    </div>
  );
}

function ManagerEditor({ open, onClose, facilityId, editing, onSaved }) {
  const isEdit = !!editing;
  const [name, setName] = useState("");
  const [initials, setInitials] = useState("");
  const [phone, setPhone] = useState("");
  const [color, setColor] = useState(PALETTE[0]);
  const [sourceUserId, setSourceUserId] = useState(null);
  const [search, setSearch] = useState("");
  const [searchRows, setSearchRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name || ""); setInitials(editing.initials || "");
      setPhone(editing.phone || ""); setColor(editing.color || PALETTE[0]);
      setSourceUserId(editing.source_user_id || null);
    } else {
      setName(""); setInitials(""); setPhone(""); setColor(PALETTE[0]); setSourceUserId(null);
    }
    setSearch(""); setSearchRows([]);
  }, [open, editing]);

  useEffect(() => {
    if (!open || isEdit) return;
    const q = search.trim();
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
        setSearchRows(r.data || []);
      } catch (e) { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [search, open, isEdit]);

  const pickUser = (u) => {
    setName(u.name || ""); setPhone(u.phone || "");
    setInitials(initialsFromName(u.name));
    setSourceUserId(u.id);
    setSearch("");
  };

  const save = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        facility_id: facilityId,
        name: name.trim(),
        initials: (initials || initialsFromName(name)).slice(0, 3).toUpperCase(),
        phone: phone.trim(),
        color,
        source_user_id: sourceUserId,
      };
      let r;
      if (isEdit) r = await api.patch(`/oncall/managers/${editing.id}`, payload);
      else r = await api.post("/oncall/managers", payload);
      onSaved?.(r.data);
      toast.success(isEdit ? "Updated" : "Added");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit on-call manager" : "Add on-call manager"}</DialogTitle>
        </DialogHeader>

        {!isEdit && (
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Search across all facilities</Label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-8"
                placeholder="Search name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="oncall-user-search"
              />
            </div>
            {searchRows.length > 0 && (
              <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-100" data-testid="oncall-search-results">
                {searchRows.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className="w-full text-left p-2 hover:bg-slate-50 text-xs flex items-center justify-between gap-2"
                    onClick={() => pickUser(u)}
                    data-testid={`oncall-search-row-${u.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{u.name}</div>
                      <div className="text-slate-500 truncate">{u.email}</div>
                    </div>
                    <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                      u.role === "manager" ? "bg-blue-100 text-blue-800" :
                      u.role === "admin" ? "bg-amber-100 text-amber-800" :
                      "bg-slate-100 text-slate-700"
                    }`}>{u.role}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="text-[11px] text-slate-400">Or fill the fields manually below.</div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="oncall-mgr-name" />
          </div>
          <div>
            <Label className="text-xs">Initials</Label>
            <Input value={initials} maxLength={3}
              onChange={(e) => setInitials(e.target.value.toUpperCase())}
              placeholder={initialsFromName(name)}
              data-testid="oncall-mgr-initials"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs">Phone</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 (555) 123-4567"
            data-testid="oncall-mgr-phone"
          />
        </div>

        <div>
          <Label className="text-xs">Colour</Label>
          <div className="flex gap-2 mt-1 flex-wrap">
            {PALETTE.map((c) => (
              <button
                key={c} type="button"
                className={`w-7 h-7 rounded-full ring-2 transition ${color === c ? "ring-slate-900" : "ring-transparent hover:ring-slate-300"}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
                data-testid={`oncall-color-${c}`}
              />
            ))}
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} data-testid="oncall-mgr-cancel">Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={saving} data-testid="oncall-mgr-save">
            <Save size={13} className="mr-1" /> {saving ? "Saving…" : isEdit ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
